import { api_fetch, api_fetch_throws } from "@/api/client.tsx"
import { Deduction } from "@/api/deduction.tsx"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"
import { useState, useEffect } from "preact/hooks"
import { Split, TransactionJson } from "@/api"
import { nothing_to_code_notice, prepayment_coding_notice, remaining_split_cents, split_sign } from "@/utils/transaction_amount"

/** Stable key so refetched splits (same transaction id) re-sync local SplitForm state. */
export function transaction_splits_sync_key(splits: Split[]): string {
  return splits
    .map(
      (s: Split) =>
        `${s.id}:${s.amount}:${s.account_id ?? ""}:${s.execution_date ?? ""}:${s.retailer_name ?? ""}`
    )
    .join("|")
}
import { SplitFormHeader, SplitCard, AutoSplitWizard } from "./split_components"
import { toastResult } from "@/toast"
import { toast } from "@/components/ui/use-toast"
import { buildPromotionSearchSeedFromTransaction, PromotionMatch } from "@/accounting_detail/PromotionSelector.tsx"
import { UserState } from "@/auth/user.tsx"
import { OrgConfigState, loadOrgFieldConfigs } from "@/org_config/state.tsx"
import { Backup } from "@/api/backup"
import { OrgOption } from "@/api/org_options"
import { matchProducts, ProductMatchResult } from "@/api/product_catalog"
import { AutoSplitModal } from "./split_components/auto_split_modal"
import {
  AutoSplitOptions,
  getUserAutoSplitPreferences,
  setUserAutoSplitPreferences,
} from "./split_components/auto_split_preferences"
import { AutoSplitCandidate, MatchStatus } from "./split_components/auto_split_wizard_types"
import { execution_date_for_date_input } from "./split_components/execution_date_input"

interface SplitFormProps {
  transaction: TransactionJson
}

interface SplitFormState {
  splits: Split[]
  isReadyToSave: boolean
  editingSplitId: string | null
}

/** Pink to match the "prepayment" kind badge, so the two read as one explanation. */
function PrepaymentCodingNotice({ message }: { message: string }) {
  return <div class="rounded-md border border-pink-200 bg-pink-50 px-4 py-3 text-sm text-pink-900">{message}</div>
}

function NothingToCodeNotice({ message }: { message: string }) {
  return <div class="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">{message}</div>
}

export function SplitForm({ transaction }: SplitFormProps) {
  const user = UserState.use()
  const [state, setState] = useState<SplitFormState>({
    splits: [],
    isReadyToSave: false,
    editingSplitId: null,
  })
  const [promotionMatches, setPromotionMatches] = useState<PromotionMatch[]>([])
  const [promotionMatchesLoaded, setPromotionMatchesLoaded] = useState(false)
  const orgConfig = OrgConfigState.use()

  // Auto-split state
  const [autoSplitModalOpen, setAutoSplitModalOpen] = useState(false)
  const [backupData, setBackupData] = useState<Backup[]>([])
  const [backupLoading, setBackupLoading] = useState(false)
  const [retailerOptions, setRetailerOptions] = useState<OrgOption[]>([])
  const [productLineOptions, setProductLineOptions] = useState<OrgOption[]>([])
  const [productCategoryOptions, setProductCategoryOptions] = useState<OrgOption[]>([])
  const [autoSplitWizardOpen, setAutoSplitWizardOpen] = useState(false)
  const [autoSplitCandidates, setAutoSplitCandidates] = useState<AutoSplitCandidate[]>([])

  // Get user key for preferences
  const userKey = (user?.id || user?.email || "anon") as string

  // Sync splits state when transaction changes
  useEffect(() => {
    let splits = transaction.splits
    setState(prev => ({
      ...prev,
      splits,
      isReadyToSave: false,
      editingSplitId: null,
    }))
  }, [transaction.id, transaction_splits_sync_key(transaction.splits)])

  // Fetch promotion matches once when component mounts
  // This data is then passed down to all SplitCard components to avoid duplicate API calls
  useEffect(() => {
    async function loadPromotionMatches() {
      if (!promotionMatchesLoaded) {
        try {
          const result = await api_fetch<PromotionMatch[]>("/promotion-matches", {
            method: "POST",
            body: {
              deduction_id: transaction.id,
              limit: 50,
            },
          })

          if (result.ok) {
            setPromotionMatches(result.value.data)
            setPromotionMatchesLoaded(true)
          }
        } catch (error) {
          console.warn("Failed to load promotion matches:", error)
        }
      }
    }

    async function loadData() {
      await loadPromotionMatches()
      await loadOrgFieldConfigs()
    }

    loadData()
  }, [transaction.id, promotionMatchesLoaded])

  function addNewSplit() {
    // Work in cents to avoid floating point precision errors
    const split_total_cents = state.splits.reduce((sum, row) => sum + Math.round(row.amount * 100), 0)

    // Remaining amount against the coding target (invoices code their discount, not the face amount)
    const remaining_cents = remaining_split_cents(transaction.kind, transaction.invoice_amount, transaction.discount_amount, split_total_cents)
    const remainingAmount = Math.round(remaining_cents) / 100

    const newSplit = createSingleSplit(transaction as Deduction, remainingAmount)
    setState(prev => ({
      ...prev,
      splits: [...prev.splits, newSplit],
      isReadyToSave: true,
      editingSplitId: newSplit.id,
    }))
  }

  function updateSplit(splitId: string, field: keyof Split, value: any) {
    setState(prev => ({
      ...prev,
      splits: prev.splits.map(split => (split.id === splitId ? { ...split, [field]: value } : split)),
      isReadyToSave: true,
    }))
  }

  function deleteSplit(splitId: string) {
    setState(prev => ({
      ...prev,
      splits: prev.splits.filter(split => split.id !== splitId),
      isReadyToSave: true,
      editingSplitId: prev.editingSplitId === splitId ? null : prev.editingSplitId,
    }))
  }

  function duplicateSplit(splitId: string) {
    const sourceSplit = state.splits.find(s => s.id === splitId)
    if (!sourceSplit) return

    // Work in cents to avoid floating point precision errors (same as addNewSplit)
    const split_total_cents = state.splits.reduce((sum, row) => sum + Math.round(row.amount * 100), 0)
    const remaining_cents = remaining_split_cents(transaction.kind, transaction.invoice_amount, transaction.discount_amount, split_total_cents)
    const source_amount_cents = Math.round(sourceSplit.amount * 100)
    // Cap amount to remaining: use the value closer to zero when both are negative
    const duplicated_amount_cents = Math.max(remaining_cents, source_amount_cents)
    const duplicatedAmount = Math.round(duplicated_amount_cents) / 100

    const newSplit: Split = {
      ...sourceSplit,
      id: `manual-${Date.now()}-${Math.random()}`,
      amount: duplicatedAmount,
    }

    setState(prev => ({
      ...prev,
      splits: [...prev.splits, newSplit],
      isReadyToSave: true,
      editingSplitId: newSplit.id,
    }))
  }

  function startEditing(splitId: string) {
    setState(prev => ({
      ...prev,
      editingSplitId: splitId,
    }))
  }

  function stopEditing() {
    setState(prev => ({
      ...prev,
      editingSplitId: null,
    }))
  }

  async function fetchBackupData(): Promise<Backup[]> {
    try {
      const data = await api_fetch_throws<Backup[]>(`/deduction/${transaction.id}/backup`)
      return data
    } catch (error) {
      console.warn("Failed to fetch backup data:", error)
      return []
    }
  }

  async function fetchOrgOptions(fieldName: string): Promise<OrgOption[]> {
    try {
      const result = await api_fetch<OrgOption[]>("/org-options", {
        params: { field_name: fieldName },
        use_cache: true,
      })
      if (result.ok) {
        return result.value.data.filter((opt: OrgOption) => opt.is_active)
      }
    } catch (error) {
      console.warn(`Failed to fetch org options for ${fieldName}:`, error)
    }
    return []
  }

  async function handleAutoSplit() {
    setBackupLoading(true)
    try {
      const data = await fetchBackupData()
      setBackupData(data)

      // Always fetch org options for retailer_name, product_line, and product_category
      // These fields commonly have dropdown options for fuzzy matching
      // Store in local variables to avoid state timing issues
      const [fetchedRetailerOptions, fetchedProductLineOptions, fetchedProductCategoryOptions] = await Promise.all([
        fetchOrgOptions("retailer_name"),
        fetchOrgOptions("product_line"),
        fetchOrgOptions("product_category"),
      ])
      
      setRetailerOptions(fetchedRetailerOptions)
      setProductLineOptions(fetchedProductLineOptions)
      setProductCategoryOptions(fetchedProductCategoryOptions)

      if (data.length === 0 && !transaction.backup_s3_uri) {
        // No backup data and no backup file
        toast({
          title: "No backup data available",
          description: "There is no parsed backup data or backup file available for auto-splitting.",
          variant: "destructive",
        })
      } else {
        // Always show the modal (pre-filled with saved preferences)
        setAutoSplitModalOpen(true)
      }
    } finally {
      setBackupLoading(false)
    }
  }

  function handleBackupParsed() {
    // Re-fetch backup data after parsing
    fetchBackupData().then((data) => {
      setBackupData(data)
      if (data.length === 0) {
        toast({
          title: "No backup data found",
          description: "The backup file was parsed but no data rows were extracted.",
          variant: "destructive",
        })
        setAutoSplitModalOpen(false)
      }
    })
  }

  async function handleGenerateSplits(
    options: AutoSplitOptions,
    backupDataOverride?: Backup[],
    retailerOptionsOverride?: OrgOption[],
    productLineOptionsOverride?: OrgOption[],
    productCategoryOptionsOverride?: OrgOption[]
  ) {
    // Use overrides if provided (for direct calls), otherwise use state (for modal calls)
    const dataToUse = backupDataOverride ?? backupData
    const retailerOptsToUse = retailerOptionsOverride ?? retailerOptions
    const productLineOptsToUse = productLineOptionsOverride ?? productLineOptions
    const productCategoryOptsToUse = productCategoryOptionsOverride ?? productCategoryOptions

    if (dataToUse.length === 0) {
      toast({
        title: "No backup data",
        description: "Cannot generate splits without backup data.",
        variant: "destructive",
      })
      return
    }

    // Save grouping preferences for next time (reason code is per-deduction, not saved)
    setUserAutoSplitPreferences(userKey, {
      splitByRetailer: options.splitByRetailer,
      splitByProductLine: options.splitByProductLine,
      splitByExecutionDate: options.splitByExecutionDate,
      splitByAdminFee: options.splitByAdminFee,
    })

    // Generate candidate splits from backup data with mapping metadata
    const candidates = generateAutoSplitCandidates(
      dataToUse,
      options,
      transaction as Deduction,
      retailerOptsToUse,
      productLineOptsToUse,
      productCategoryOptsToUse
    )
    const enrichedCandidates = options.splitByProductLine
      ? await enrichCandidatesWithCatalogMatches(candidates)
      : candidates

    if (enrichedCandidates.length === 0) {
      toast({
        title: "No splits generated",
        description: "The backup data did not produce any splits with the selected grouping options.",
        variant: "destructive",
      })
      return
    }

    setAutoSplitCandidates(enrichedCandidates)
    setAutoSplitWizardOpen(true)
  }

  /**
   * Accrual-bank split: the server groups the parsed backup by customer bucket and codes deep
   * discounts to slotting, the rest to promotional allowance (`/deduction/{id}/accrual-bank-split`).
   * Draft splits are appended for review; nothing is saved until "Save All".
   */
  async function handleBucketSplit() {
    const res = await api_fetch<bucket_split_proposal>(`/deduction/${transaction.id}/accrual-bank-split`)
    if (!res.ok) {
      toast({ title: "Could not propose a split", description: res.error.message, variant: "destructive" })
      return
    }
    const proposal = res.value.data
    if (proposal.splits.length === 0) {
      toast({
        title: "No splits proposed",
        description:
          proposal.unbucketed_lines > 0
            ? `${proposal.unbucketed_lines} backup lines belong to a source with no accrual-bank bucket.`
            : "There is no parsed backup to group.",
        variant: "destructive",
      })
      return
    }
    const sign = split_sign(transaction.kind, transaction.invoice_amount)
    const newSplits: Split[] = proposal.splits.map((p: bucket_split_proposal["splits"][number], index: number) => ({
      id: `manual-${Date.now()}-bucket-${index}`,
      deduction_id: transaction.id,
      amount: sign * p.amount,
      retailer_name: p.bucket_name,
      product_line: "",
      execution_date: "",
      customer_name: "",
      promo_type: "",
      promo_sub_type: "",
      code_description: `${p.line_count} backup lines: ${p.retailers.slice(0, 4).join(", ")}${p.retailers.length > 4 ? "…" : ""}`,
      product_category: "",
      retailer_invoice_number: "",
      account_id: p.account_id,
      expense_account: p.account_label,
      name: p.account_label,
    }))
    setState((prev) => ({ ...prev, splits: [...prev.splits, ...newSplits], isReadyToSave: true, editingSplitId: newSplits[0]?.id || null }))
    const notes: string[] = []
    if (proposal.remainder !== 0) notes.push(`${formatAmount(proposal.remainder)} of the deduction is not explained by the backup lines.`)
    if (proposal.unbucketed_lines > 0) notes.push(`${proposal.unbucketed_lines} lines had no bucket and were left out.`)
    toast({
      title: `Proposed ${newSplits.length} split${newSplits.length === 1 ? "" : "s"} by customer bucket`,
      description: `${notes.join(" ")} Review and click "Save All" to apply.`.trim(),
    })
  }

  function handleFinalizeWizardCandidates(candidates: AutoSplitCandidate[]) {
    const newSplits = materializeCandidatesToSplits(candidates, transaction as Deduction)
    if (newSplits.length === 0) {
      toast({
        title: "No splits finalized",
        description: "There were no candidate splits to apply.",
        variant: "destructive",
      })
      return
    }

    setState((prev) => ({
      ...prev,
      splits: [...prev.splits, ...newSplits],
      isReadyToSave: true,
      editingSplitId: newSplits[0]?.id || null,
    }))

    toast({
      title: "Splits generated",
      description: `Created ${newSplits.length} draft split${newSplits.length === 1 ? "" : "s"}. Review and click "Save All" to apply.`,
    })
    setAutoSplitModalOpen(false)
    setAutoSplitWizardOpen(false)
  }

  async function handleSubmit() {
    let data = state.splits.map(split_row => {
      let split: any = { ...split_row }
      if (split.id.startsWith("manual")) {
        delete split["id"]
      }
      delete split["customer_name"]
      delete split["name"] // this is account name, but its unneeded because we get the account from account_id

      // Ensure amount is a number, not a string
      if (split.amount !== undefined && split.amount !== null) {
        split.amount = typeof split.amount === "string" ? parseFloat(split.amount) : split.amount
      }

      for (const key of Object.keys(split)) {
        if (split[key] === "") {
          delete split[key]
        }
      }
      return split
    })

    const res = await api_fetch("/split-deductions", {
      method: "POST",
      body: [
        {
          transaction_id: transaction.id,
          splits: data,
        },
      ],
    })
    toastResult(res, `Saved ${state.splits.length} accounting codes`, "Failed to save accounting codes")
    if (!res.ok) {
      return
    }
    triggerRefreshFlag()
    setState(prev => ({
      ...prev,
      isReadyToSave: false,
      editingSplitId: null, // Collapse any open edit views
    }))
  }

  const savedPreferences = getUserAutoSplitPreferences(userKey)

  // A UNFI prepayment's cash is never coded; say so instead of offering a form that the server
  // (and the `prepayment_split_guard` trigger) would refuse.
  const prepayment_notice = prepayment_coding_notice(transaction)
  if (prepayment_notice?.blocked) {
    return (
      <div className="w-full pt-8" data-accounting-split-form>
        <PrepaymentCodingNotice message={prepayment_notice.message} />
      </div>
    )
  }

  // An invoice with no early-pay discount has a $0.00 coding amount. Lines already saved on it
  // stay editable so they can be removed.
  const nothing_to_code = transaction.splits.length === 0 ? nothing_to_code_notice(transaction) : null
  if (nothing_to_code) {
    return (
      <div className="w-full pt-8" data-accounting-split-form>
        <NothingToCodeNotice message={nothing_to_code} />
      </div>
    )
  }

  return (
    <div className="w-full space-y-4 pt-8" data-accounting-split-form>
      {prepayment_notice ? <PrepaymentCodingNotice message={prepayment_notice.message} /> : null}
      <SplitFormHeader
        isReadyToSave={state.isReadyToSave}
        onAddSplit={addNewSplit}
        onAutoSplit={handleAutoSplit}
        onBucketSplit={handleBucketSplit}
        onSave={handleSubmit}
      />

      <div className="space-y-3">
        {state.splits.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No splits created yet. Click "Add Split" to get started.</div>
        ) : (
          state.splits.map((split, index) => (
            <SplitCard
              key={split.id}
              split={split}
              index={index}
              isEditing={state.editingSplitId === split.id}
              onUpdate={(field, value) => updateSplit(split.id, field, value)}
              onDelete={() => deleteSplit(split.id)}
              onDuplicate={duplicateSplit}
              onStartEdit={() => startEditing(split.id)}
              onStopEdit={stopEditing}
              promotionMatches={promotionMatches}
              hasUnsavedChanges={state.isReadyToSave}
              orgFieldConfigs={orgConfig.orgFieldConfigs}
              orgId={user?.org_id ?? undefined}
              promotionSearchContextSeed={buildPromotionSearchSeedFromTransaction(transaction)}
            />
          ))
        )}
      </div>

      <AutoSplitModal
        open={autoSplitModalOpen}
        onOpenChange={setAutoSplitModalOpen}
        backupData={backupData}
        backupS3Uri={transaction.backup_s3_uri || null}
        deductionId={transaction.id}
        savedPreferences={savedPreferences}
        orgFieldConfigs={orgConfig.orgFieldConfigs}
        promotionSearchContextSeed={buildPromotionSearchSeedFromTransaction(transaction)}
        onGenerateSplits={handleGenerateSplits}
        onBackupParsed={handleBackupParsed}
      />
      <AutoSplitWizard
        open={autoSplitWizardOpen}
        onOpenChange={setAutoSplitWizardOpen}
        initialCandidates={autoSplitCandidates}
        transactionAmount={transaction.invoice_amount}
        retailerOptions={retailerOptions}
        productLineOptions={productLineOptions}
        productCategoryOptions={productCategoryOptions}
        onFinalize={handleFinalizeWizardCandidates}
      />
    </div>
  )
}

export function createSingleSplit(deduction: Deduction & { customer_name?: string }, remainingAmount?: number): Split {
  return {
    id: `manual-${Date.now()}-${Math.random()}`,
    deduction_id: deduction.id,
    promo_type: "",
    promo_sub_type: "",
    code_description: "",
    amount: remainingAmount ?? -deduction.invoice_amount,
    customer_name: deduction.customer_name || "",
    retailer_name: deduction.retailer_name || "",
    product_line: "",
    product_category: "",
    execution_date: "",
    retailer_invoice_number: "",
    account_id: undefined,
    promotion_id: deduction.promotion_id || undefined,
    name: "Uncoded",
  }
}

interface BackupGroup {
  total_cents: number
  sample: Backup
  source_count: number
  retailer_name: string | null
  product_line: string | null
  product_category: string | null
  execution_date: string | null
  admin_fee: number | null
  raw_retailer: string | null
  raw_product_line: string | null
  raw_upc_key: string | null
  retailer_match_status: MatchStatus
  product_line_match_status: MatchStatus
  product_category_match_status: MatchStatus
}

/**
 * Extract meaningful words from a string for matching.
 * Removes codes, locations, and normalizes.
 */
function extractMatchWords(value: string, minLength: number = 3): string[] {
  // Normalize: lowercase, remove parentheses content
  let normalized = value.toLowerCase()
  // Normalize punctuation and common abbreviations.
  normalized = normalized.replace(/['’`]/g, "")
  normalized = normalized.replace(/\bmkt\b/g, "market")
  // Remove parenthetical content like (NSM), (ABC)
  normalized = normalized.replace(/\([^)]*\)/g, "")
  // Remove leading codes like "R13590-"
  normalized = normalized.replace(/^[a-z]*\d+[-\s]*/i, "")
  // Remove leading numbered prefixes like "527. " and repeated coded segments.
  normalized = normalized.replace(/^\d+\.\s*/, "")
  normalized = normalized.replace(/^(?:[a-z]?\d+[-.\s]+)+/i, "")
  // Remove trailing location info like ", CITY, ST" or ", ST"
  normalized = normalized.replace(/,\s*[^,]+,\s*[a-z]{2}\s*$/i, "")
  normalized = normalized.replace(/,\s*[a-z]{2}\s*$/i, "")
  
  // Split into words and filter
  const words = normalized
    .split(/[\s\-_,./]+/)
    .map(w => w.trim())
    .filter(w => w.length >= minLength)
    .filter(w => !/\d/.test(w))
    .filter(w => !["the", "and", "for", "og2", "og3"].includes(w))
  
  return words
}

function countWordOverlap(leftWords: string[], rightWords: string[]): number {
  if (leftWords.length === 0 || rightWords.length === 0) return 0
  return rightWords.filter((word) => leftWords.some((left) => wordsRoughlyMatch(left, word))).length
}

const WEAK_RETAILER_TOKENS = new Set([
  "food",
  "foods",
  "market",
  "markets",
  "store",
  "stores",
  "management",
  "inc",
  "llc",
  "co",
  "company",
  "edi",
  "group",
  "corp",
  "corporation",
  "holdings",
])

function isWeakRetailerToken(word: string): boolean {
  return WEAK_RETAILER_TOKENS.has(word)
}

function countStrongWordOverlap(leftWords: string[], rightWords: string[]): number {
  if (leftWords.length === 0 || rightWords.length === 0) return 0
  return rightWords
    .filter((word) => !isWeakRetailerToken(word))
    .filter((word) => leftWords.some((left) => wordsRoughlyMatch(left, word))).length
}

function normalizeRetailerPhrase(value: string): string {
  return extractMatchWords(value, 2).join(" ").trim()
}

function getEditDistance(a: string, b: string): number {
  if (a === b) return 0
  const matrix: number[][] = Array.from({ length: a.length + 1 }, () => Array.from({ length: b.length + 1 }, () => 0))

  for (let i = 0; i <= a.length; i++) matrix[i][0] = i
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      )
    }
  }

  return matrix[a.length][b.length]
}

function wordsRoughlyMatch(left: string, right: string): boolean {
  if (left === right) return true
  if (left.length < 4 || right.length < 4) return false
  const maxLen = Math.max(left.length, right.length)
  const allowedDistance = maxLen >= 7 ? 2 : 1
  return getEditDistance(left, right) <= allowedDistance
}

/**
 * Check if any pattern from an option's match_patterns matches the value.
 * Returns true if any pattern is found in the normalized value.
 */
function matchesOptionPatterns(normalizedValue: string, patterns: string[], strictRetailerMode: boolean = false): boolean {
  if (!patterns || patterns.length === 0) return false
  
  // Add spaces around value for boundary matching
  const paddedValue = ` ${normalizedValue} `
  
  for (const pattern of patterns) {
    const normalizedPattern = pattern.toLowerCase().trim()
    if (!normalizedPattern) continue

    if (strictRetailerMode) {
      const patternWords = normalizedPattern
        .split(/[\s\-_,./]+/)
        .map((w) => w.trim())
        .filter((w) => w.length >= 2)
      const hasStrongWord = patternWords.some((w) => !isWeakRetailerToken(w))
      if (!hasStrongWord) {
        continue
      }
    }
    
    // Check if pattern appears in the value
    if (paddedValue.includes(normalizedPattern) || normalizedValue.includes(normalizedPattern)) {
      return true
    }
  }
  
  return false
}

/**
 * Fuzzy match a value to the closest option in a list.
 * Uses OrgOption.match_patterns for pattern-based matching.
 * Falls back to multi-word matching for retailer names.
 */
function fuzzyMatchOption(
  value: string | null | undefined,
  options: OrgOption[],
  mode: "default" | "retailer" = "default"
): string | null {
  if (!value || options.length === 0) return null

  const normalizedValue = value.toLowerCase().trim()
  const valueWords = extractMatchWords(value, 2)
  const strictRetailerMode = mode === "retailer"

  if (strictRetailerMode) {
    const normalizedRetailerValue = normalizeRetailerPhrase(value)
    if (normalizedRetailerValue) {
      const exactRetailerPhrase = options.find((opt) => normalizeRetailerPhrase(opt.option_value) === normalizedRetailerValue)
      if (exactRetailerPhrase) {
        return exactRetailerPhrase.option_value
      }

      const phraseCandidates = options
        .map((opt) => ({
          option: opt,
          phrase: normalizeRetailerPhrase(opt.option_value),
        }))
        .filter((item) => item.phrase.length > 0)
        .filter(
          (item) =>
            normalizedRetailerValue.includes(item.phrase) ||
            item.phrase.includes(normalizedRetailerValue)
        )
        .sort((a, b) => b.phrase.length - a.phrase.length)

      if (phraseCandidates.length > 0) {
        return phraseCandidates[0].option.option_value
      }
    }
  }

  // 1. Try exact match (case-insensitive)
  const exactMatch = options.find(
    (opt) => opt.option_value.toLowerCase().trim() === normalizedValue
  )
  if (exactMatch) return exactMatch.option_value

  // 2. Try pattern matching using OrgOption.match_patterns
  // Options with more specific patterns (more patterns defined) get priority
  let bestPatternMatch: OrgOption | null = null
  let bestPatternScore = 0
  
  for (const opt of options) {
    const patterns = opt.match_patterns || []
    if (matchesOptionPatterns(normalizedValue, patterns, strictRetailerMode)) {
      // Score based on number of patterns defined (more specific options win)
      // Also consider pattern length - longer patterns are more specific
      const longestPattern = patterns.reduce((max, p) => Math.max(max, p.length), 0)
      const score = patterns.length + longestPattern
      if (score > bestPatternScore) {
        bestPatternScore = score
        bestPatternMatch = opt
      }
    }
  }
  
  if (bestPatternMatch) {
    return bestPatternMatch.option_value
  }

  // 3. Fall back to multi-word matching (for retailer names like "New Seasons")
  const valueWordsLong = extractMatchWords(value, 3)

  // 3a. Prefer options that reduce to a single meaningful word that matches exactly
  // (e.g. "527. R15150-Wegmans" should win over "...-Wegmans EDI" for value "WEGMANS").
  const singleWordExact = options.find((opt) => {
    const optWords = extractMatchWords(opt.option_value, 2)
    return optWords.length === 1 && valueWords.some((vw) => wordsRoughlyMatch(vw, optWords[0]))
  })
  if (singleWordExact) {
    return singleWordExact.option_value
  }

  // 3b. Strong partial overlap match for location/suffix variations:
  // e.g. "EARTH FARE KNX" -> "R14400 - Earth Fare Management Inc"
  // and "MUSTARD SEED - AKRON" -> "R2342 - Mustard Seed Market"
  let bestOverlapOption: OrgOption | null = null
  let bestOverlapScore = 0
  for (const opt of options) {
    const optWords = extractMatchWords(opt.option_value, 2)
    const overlapCount = countWordOverlap(valueWords, optWords)
    const strongOverlapCount = countStrongWordOverlap(valueWords, optWords)
    const overlapRatio = optWords.length > 0 ? overlapCount / optWords.length : 0

    const strongOverlapThreshold = strictRetailerMode ? 2 : 1
    if (overlapCount >= 2 && overlapRatio >= 0.5 && strongOverlapCount >= strongOverlapThreshold) {
      const score = strongOverlapCount * 100 + overlapCount * 10 + overlapRatio
      if (score > bestOverlapScore) {
        bestOverlapScore = score
        bestOverlapOption = opt
      }
    }
  }
  if (bestOverlapOption) {
    return bestOverlapOption.option_value
  }
  
  for (const opt of options) {
    const optWords = extractMatchWords(opt.option_value, 2)
    
    if (optWords.length > 1) {
      let matchingWords = 0
      for (const optWord of optWords) {
        if (valueWordsLong.some(vw => vw === optWord)) {
          matchingWords++
        }
      }
      
      if (matchingWords === optWords.length) {
        // All words match
        return opt.option_value
      }
    }
  }

  // No good match found
  return null
}

function normalizeLookupKey(value: string | null | undefined): string | null {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const digitsOnly = trimmed.replace(/\D/g, "")
  return digitsOnly.length >= 6 ? digitsOnly : null
}

function getLookupKeysForBackupRow(row: Backup): string[] {
  const rawCandidates = [row.upc, row.product_number, row.inventory_number]
  const ordered = rawCandidates.map(normalizeLookupKey).filter((key): key is string => !!key)
  return Array.from(new Set(ordered))
}

function findOptionByLookupKeys(lookupKeys: string[], options: OrgOption[]): string | null {
  if (lookupKeys.length === 0 || options.length === 0) return null

  for (const key of lookupKeys) {
    for (const option of options) {
      const patterns = option.match_patterns || []
      if (patterns.some((pattern) => normalizeLookupKey(pattern) === key)) {
        return option.option_value
      }
    }
  }

  return null
}

function isKnownUnknownValue(value: string | null | undefined): boolean {
  return !value || value.trim().length === 0 || value.trim().toLowerCase() === "unknown"
}

function optionIncludesValue(value: string | null | undefined, options: OrgOption[]): boolean {
  if (isKnownUnknownValue(value)) return false
  const normalizedValue = value!.trim().toLowerCase()
  return options.some((option) => option.option_value.trim().toLowerCase() === normalizedValue)
}

function deriveMatchStatusFromOptions(
  value: string | null | undefined,
  hasOptionConstraint: boolean,
  options: OrgOption[]
): MatchStatus {
  if (isKnownUnknownValue(value)) return "unmatched"
  if (!hasOptionConstraint) return "fallback"
  return optionIncludesValue(value, options) ? "matched" : "unmatched"
}

function deriveCandidateMatchStatus(
  candidate: Pick<AutoSplitCandidate, "retailer_name" | "product_line" | "product_category" | "option_enforced">,
  retailerOptions: OrgOption[],
  productLineOptions: OrgOption[],
  productCategoryOptions: OrgOption[]
): AutoSplitCandidate["match_status"] {
  return {
    retailer: deriveMatchStatusFromOptions(
      candidate.retailer_name,
      candidate.option_enforced.retailer,
      retailerOptions
    ),
    product_line: deriveMatchStatusFromOptions(
      candidate.product_line,
      candidate.option_enforced.product_line,
      productLineOptions
    ),
    product_category: deriveMatchStatusFromOptions(
      candidate.product_category,
      candidate.option_enforced.product_category,
      productCategoryOptions
    ),
  }
}

function applyCatalogMatchToCandidate(candidate: AutoSplitCandidate, match: ProductMatchResult | undefined): AutoSplitCandidate {
  if (!match?.product_id) return candidate
  return {
    ...candidate,
    product_id: match.product_id,
    product_line_id: match.product_line_id,
    product_category_id: match.product_category_id,
    product_line: match.product_line || candidate.product_line,
    product_category: match.product_category || candidate.product_category,
    match_status: {
      ...candidate.match_status,
      product_line: match.product_line ? "matched" : candidate.match_status.product_line,
      product_category: match.product_category ? "matched" : candidate.match_status.product_category,
    },
  }
}

async function enrichCandidatesWithCatalogMatches(candidates: AutoSplitCandidate[]): Promise<AutoSplitCandidate[]> {
  const response = await matchProducts(
    candidates.map((candidate) => ({
      upc: candidate.raw_upc_key || null,
      product_number: candidate.raw_upc_key || null,
      inventory_number: null,
      product_description: candidate.raw_product_description || candidate.product_line || null,
    }))
  )
  if (!response.ok) {
    return candidates
  }
  return candidates.map((candidate, index) => applyCatalogMatchToCandidate(candidate, response.value.data.matches[index]))
}

function generateAutoSplitCandidates(
  backup: Backup[],
  options: AutoSplitOptions,
  deduction: Deduction,
  retailerOptions: OrgOption[],
  productLineOptions: OrgOption[],
  productCategoryOptions: OrgOption[]
): AutoSplitCandidate[] {
  // Build groups by selected fields
  const groups = new Map<string, BackupGroup>()

  for (const row of backup) {
    const keyParts: string[] = []

    // Get raw values from backup
    const rawRetailer = row.retailer_name || null
    const rawProductLine = row.product_description || null
    const rawExecutionDate = row.execution_date || null
    const rawAdminFee = row.admin_fee ?? null
    const lookupKeys = getLookupKeysForBackupRow(row)
    const retailerHasOptionConstraint = options.splitByRetailer && retailerOptions.length > 0
    const productLineHasOptionConstraint = options.splitByProductLine && productLineOptions.length > 0
    const productCategoryHasOptionConstraint = options.splitByProductLine && productCategoryOptions.length > 0

    // Apply fuzzy matching if options are available
    const matchedRetailer = retailerHasOptionConstraint
      ? fuzzyMatchOption(rawRetailer, retailerOptions, "retailer")
      : null
    const retailer = options.splitByRetailer
      ? (matchedRetailer || rawRetailer || "Unknown")
      : null

    const matchedProductLineByLookup = findOptionByLookupKeys(lookupKeys, productLineOptions)
    const matchedProductCategoryByLookup = findOptionByLookupKeys(lookupKeys, productCategoryOptions)
    const matchedProductLineByText = fuzzyMatchOption(rawProductLine, productLineOptions)

    const productLine = options.splitByProductLine
      ? (productLineHasOptionConstraint
          ? matchedProductLineByLookup || matchedProductLineByText || rawProductLine || "Unknown"
          : rawProductLine || "Unknown")
      : null

    const productCategory = options.splitByProductLine
      ? (productCategoryHasOptionConstraint
          ? matchedProductCategoryByLookup || ""
          : "")
      : null

    const executionDate = options.splitByExecutionDate ? (rawExecutionDate || "Unknown") : null
    const adminFee = options.splitByAdminFee ? (rawAdminFee ?? 0) : null

    if (options.splitByRetailer) keyParts.push(retailer || "Unknown")
    if (options.splitByProductLine) keyParts.push(productLine || "Unknown")
    if (options.splitByExecutionDate) keyParts.push(executionDate || "Unknown")
    if (options.splitByAdminFee) keyParts.push(adminFee?.toString() || "0")

    const key = keyParts.join("|") || "all"
    const rowTotal = row.total ?? 0
    // Convert to cents for precision
    const rowTotalCents = Math.round(rowTotal * 100)

    const existing = groups.get(key)
    if (existing) {
      existing.total_cents += rowTotalCents
      existing.source_count += 1
      if (!existing.product_category && matchedProductCategoryByLookup) {
        existing.product_category = matchedProductCategoryByLookup
        existing.product_category_match_status = deriveMatchStatusFromOptions(
          matchedProductCategoryByLookup,
          productCategoryHasOptionConstraint,
          productCategoryOptions
        )
      }
      if (!existing.raw_upc_key && lookupKeys[0]) {
        existing.raw_upc_key = lookupKeys[0]
      }
    } else {
      groups.set(key, {
        total_cents: rowTotalCents,
        sample: row,
        source_count: 1,
        retailer_name: retailer,
        product_line: productLine,
        product_category: productCategory,
        execution_date: executionDate,
        admin_fee: adminFee,
        raw_retailer: rawRetailer,
        raw_product_line: rawProductLine,
        raw_upc_key: lookupKeys[0] || null,
        retailer_match_status: deriveMatchStatusFromOptions(retailer, retailerHasOptionConstraint, retailerOptions),
        product_line_match_status: deriveMatchStatusFromOptions(
          productLine,
          productLineHasOptionConstraint,
          productLineOptions
        ),
        product_category_match_status: deriveMatchStatusFromOptions(
          productCategory,
          productCategoryHasOptionConstraint,
          productCategoryOptions
        ),
      })
    }
  }

  // Convert groups to candidates
  const candidates: AutoSplitCandidate[] = []
  let index = 0

  for (const group of groups.values()) {
    // Convert cents back to dollars
    const amount = group.total_cents / 100

    // Skip zero-amount groups
    if (amount === 0) continue

    // Get the best retailer and product line values
    // Priority: grouped value > fuzzy matched > raw backup value > empty
    const rawRetailer = group.sample.retailer_name || ""
    const matchedRetailer = fuzzyMatchOption(rawRetailer, retailerOptions, "retailer")
    const finalRetailerName = group.retailer_name || matchedRetailer || rawRetailer

    const rawProductLine = group.sample.product_description || ""
    const lookupKeys = getLookupKeysForBackupRow(group.sample)
    const matchedProductLineByLookup = findOptionByLookupKeys(lookupKeys, productLineOptions)
    const matchedProductLine = fuzzyMatchOption(rawProductLine, productLineOptions)
    const finalProductLine = group.product_line || matchedProductLine || rawProductLine
    const finalProductCategory = group.product_category || findOptionByLookupKeys(lookupKeys, productCategoryOptions) || ""
    const optionEnforced = {
      retailer: options.splitByRetailer && retailerOptions.length > 0,
      product_line: options.splitByProductLine && productLineOptions.length > 0,
      product_category: options.splitByProductLine && productCategoryOptions.length > 0,
    }

    const candidate: AutoSplitCandidate = {
      id: `candidate-${Date.now()}-${index++}-${Math.random()}`,
      source_count: group.source_count,
      source_amount: amount,
      source_amount_cents: group.total_cents,
      raw_retailer: group.raw_retailer || "",
      raw_product_description: group.raw_product_line || "",
      raw_upc_key: group.raw_upc_key || "",
      retailer_name: finalRetailerName || "Unknown",
      product_line: finalProductLine || "Unknown",
      product_category: finalProductCategory || "",
      execution_date: group.execution_date || group.sample.execution_date || "",
      promo_type: group.sample.promo_type || "",
      promo_sub_type: group.sample.promo_sub_type || "",
      account_id: options.reasonCodeId,
      expense_account: options.reasonCodeName,
      promotion_id: options.defaultPromotionId ?? deduction.promotion_id,
      retailer_invoice_number: options.defaultRetailerInvoiceNumber?.trim() || undefined,
      name: options.reasonCodeName || "Uncoded",
      match_status: {
        retailer: group.retailer_match_status,
        product_line: group.product_line_match_status,
        product_category: group.product_category_match_status,
      },
      option_enforced: optionEnforced,
    }
    candidate.match_status = deriveCandidateMatchStatus(
      candidate,
      retailerOptions,
      productLineOptions,
      productCategoryOptions
    )

    candidates.push(candidate)
  }

  return candidates
}

interface bucket_split_proposal {
  deduction_id: string
  splits: { bucket_id: string | null; bucket_name: string; account_id: string; account_label: string; amount: number; line_count: number; retailers: string[] }[]
  unbucketed_lines: number
  unbucketed_amount: number
  remainder: number
  slotting_discount_pct: number
}

function materializeCandidatesToSplits(candidates: AutoSplitCandidate[], deduction: Deduction): Split[] {
  // The transaction decides the sign, so a repayment's splits come out negative like the manual
  // form's `remaining_split_cents` default. On a deduction a candidate's *own* sign is relative to
  // that and must survive: a backup group that nets negative (a credit or reversal line) is an
  // offset against the deduction, and taking its magnitude here made the saved total exceed the
  // deduction amount even though the wizard's review step — which sums the signed cents —
  // reported no variance.
  //
  // Repayments are deliberately left as they were: every line takes the transaction's sign
  // whatever sign the backup line carries. Changing that is a separate call from this bug.
  const sign = split_sign(deduction.kind, deduction.invoice_amount)
  const keep_line_sign = deduction.kind !== "repayment"
  return candidates.map((candidate: AutoSplitCandidate, index: number) => ({
    id: `manual-${Date.now()}-${index}-${Math.random()}`,
    deduction_id: deduction.id,
    amount: sign * (keep_line_sign ? candidate.source_amount : Math.abs(candidate.source_amount)),
    retailer_name: candidate.retailer_name || "Unknown",
    product_id: candidate.product_id,
    product_line_id: candidate.product_line_id,
    product_category_id: candidate.product_category_id,
    product_line: candidate.product_line || "",
    execution_date: execution_date_for_date_input(candidate.execution_date || ""),
    customer_name: "",
    promo_type: candidate.promo_type || "",
    promo_sub_type: candidate.promo_sub_type || "",
    code_description: "",
    product_category: candidate.product_category || "",
    retailer_invoice_number: candidate.retailer_invoice_number ?? "",
    account_id: candidate.account_id,
    expense_account: candidate.expense_account,
    promotion_id: candidate.promotion_id || undefined,
    name: candidate.name || "Uncoded",
  }))
}

// Helper function to safely parse and format numbers
function formatAmount(value: number | string): string {
  if (value === "" || value === null || value === undefined) {
    return "0.00"
  }

  const num = typeof value === "string" ? parseFloat(value) : value
  if (isNaN(num)) return "0.00"

  return num.toFixed(2)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("transaction_splits_sync_key", () => {
    it("changes when split rows change", () => {
      const a: Split[] = [
        {
          id: "1",
          deduction_id: "d",
          amount: 10,
          name: "x",
          account_id: "acc1",
        },
      ]
      const b: Split[] = [{ ...a[0], account_id: "acc2" }]
      expect(transaction_splits_sync_key(a)).not.toBe(transaction_splits_sync_key(b))
    })
  })

  describe("materializeCandidatesToSplits", () => {
    const baseDeduction = { id: "ded-1", kind: "deduction", invoice_amount: -100 } as Deduction

    function minimalCandidate(overrides: Partial<AutoSplitCandidate>): AutoSplitCandidate {
      return {
        id: "c1",
        source_count: 1,
        source_amount: 100,
        source_amount_cents: 10000,
        raw_retailer: "",
        raw_product_description: "",
        raw_upc_key: "",
        retailer_name: "R",
        product_line: "P",
        product_category: "",
        execution_date: "",
        promo_type: "",
        promo_sub_type: "",
        name: "Uncoded",
        match_status: {
          retailer: "fallback",
          product_line: "fallback",
          product_category: "fallback",
        },
        option_enforced: { retailer: false, product_line: false, product_category: false },
        ...overrides,
      }
    }

    it("materializes positive amount for positive source_amount", () => {
      const splits = materializeCandidatesToSplits([minimalCandidate({ source_amount: 42.5 })], baseDeduction)
      expect(splits).toHaveLength(1)
      expect(splits[0].amount).toBe(42.5)
      expect(splits[0].amount).toBeGreaterThanOrEqual(0)
    })

    it("keeps a credit line negative instead of flipping it into a charge", () => {
      const splits = materializeCandidatesToSplits(
        [minimalCandidate({ source_amount: -30, source_amount_cents: -3000 })],
        baseDeduction
      )
      expect(splits[0].amount).toBe(-30)
    })

    it("saves the same total the review step showed", () => {
      // 120 charged less a 20 credit is a 100 deduction: taking each line's magnitude used to save
      // 140 against a 100 deduction while the review step reported no variance.
      const candidates = [
        minimalCandidate({ source_amount: 120, source_amount_cents: 12000 }),
        minimalCandidate({ source_amount: -20, source_amount_cents: -2000 }),
      ]
      const splits = materializeCandidatesToSplits(candidates, baseDeduction)
      const reviewed_cents = candidates.reduce((sum, c) => sum + c.source_amount_cents, 0)
      const saved_cents = splits.reduce((sum: number, s: Split) => sum + Math.round(s.amount * 100), 0)
      expect(saved_cents).toBe(reviewed_cents)
      expect(saved_cents).toBe(Math.round(Math.abs(baseDeduction.invoice_amount) * 100))
    })

    it("materializes negative amounts for a repayment, whatever sign the backup line carries", () => {
      // Repayment behaviour is unchanged by the deduction-side fix above.
      const repayment = { id: "ded-2", kind: "repayment", invoice_amount: 119.55 } as Deduction
      const splits = materializeCandidatesToSplits(
        [minimalCandidate({ source_amount: 108.68 }), minimalCandidate({ source_amount: -10.87 })],
        repayment
      )
      expect(splits.map((s: Split) => s.amount)).toEqual([-108.68, -10.87])
    })
  })
}
